import type { AuthType } from '@contexts/auth-provider/AuthProvider.types';

import { getRequiredAuthType } from '@shared/helpers/route-auth-map/routeAuthMap';

import type { ClearMsalCookiesOptions } from './msalInstance.types';

/**
 * MSAL authority domain patterns for detecting account types in localStorage
 */
export const MSAL_DOMAINS = {
  B2C: '.b2clogin.com',
  B2E_PRIMARY: 'login.microsoftonline.com',
  B2E_SECONDARY: 'login.windows.net',
  MSAL_PREFIX: 'msal.',
} as const;

export const MSAL_CACHE_ENCRYPTION_COOKIE = 'msal.cache.encryption';

interface AccountScanResult {
  hasB2E: boolean;
  hasB2C: boolean;
}

/**
 * Scan localStorage for MSAL account keys to detect which auth types have sessions.
 */
function scanMsalAccounts(): AccountScanResult {
  const keys = Object.keys(localStorage);
  let hasB2E = false;
  let hasB2C = false;

  for (const key of keys) {
    if (
      key.includes(MSAL_DOMAINS.B2E_PRIMARY) ||
      key.includes(MSAL_DOMAINS.B2E_SECONDARY)
    ) {
      hasB2E = true;
    }
    if (key.includes(MSAL_DOMAINS.B2C)) {
      hasB2C = true;
    }
  }

  return { hasB2E, hasB2C };
}

/**
 * Detect auth type from localStorage by scanning MSAL account keys
 *
 * MSAL stores accounts with keys containing homeAccountId:
 * - B2E: homeAccountId contains "login.microsoftonline.com" or "login.windows.net"
 * - B2C: homeAccountId contains ".b2clogin.com"
 *
 * Priority order:
 * 1. pending-auth-type (set before redirect, survives full page reload)
 * 2. Session storage auth-type (set after successful login)
 * 3. localStorage MSAL account scanning:
 *    - If BOTH B2E and B2C accounts exist → use URL to disambiguate
 *    - If only ONE type exists → use that type (preserves existing session)
 * 4. URL-based detection for strict routes (no existing session)
 * 5. Default to B2C
 *
 * When only one account type exists, it takes priority over URL-based detection.
 * This ensures that a B2E user navigating to /train (or a B2C user to /instruct)
 * stays authenticated with their existing session. Route guards (ModuleGuard)
 * handle the "no access" case by showing a not-found page.
 *
 * When BOTH account types exist, URL-based detection disambiguates:
 * - /train/* → B2C (Train is B2C-only)
 * - /instruct/* → B2E (Instruct is B2E-only)
 *
 * @param urlPath - Optional URL path for route-aware auth type detection
 * @returns Detected auth type ('b2e' | 'b2c'), defaults to 'b2c'
 */
export function detectAuthTypeFromStorage(urlPath?: string): AuthType {
  // 1. Check pending-auth-type (set before redirect, survives full page reload)
  const pendingAuthType = localStorage.getItem('pending-auth-type');
  if (pendingAuthType === 'b2e' || pendingAuthType === 'b2c') {
    return pendingAuthType;
  }

  // 2. Check session storage (set after successful login)
  const sessionAuthType = sessionStorage.getItem('auth-type');
  if (sessionAuthType === 'b2e' || sessionAuthType === 'b2c') {
    return sessionAuthType;
  }

  // 3. Scan localStorage for MSAL account keys
  const { hasB2E, hasB2C } = scanMsalAccounts();

  // 4. If BOTH account types exist, use URL to disambiguate (strict module routes)
  if (hasB2E && hasB2C && urlPath) {
    return getRequiredAuthType(urlPath) ?? 'b2c';
  }

  // 5. If only ONE account type exists, use it (preserves existing session)
  // Route guards (ModuleGuard) will handle "no access" with a not-found page
  if (hasB2E) return 'b2e';
  if (hasB2C) return 'b2c';

  // 6. No accounts found — use URL-based detection for strict routes
  if (urlPath) {
    return getRequiredAuthType(urlPath) ?? 'b2c';
  }

  // 7. Default to B2C
  return 'b2c';
}

/**
 * Get list of MSAL-related localStorage keys
 * Used for clearing accounts when switching auth types
 */
export function getMsalStorageKeys(): string[] {
  return Object.keys(localStorage).filter(
    key =>
      key.includes(MSAL_DOMAINS.B2E_PRIMARY) ||
      key.includes(MSAL_DOMAINS.B2E_SECONDARY) ||
      key.includes(MSAL_DOMAINS.B2C) ||
      key.includes(MSAL_DOMAINS.MSAL_PREFIX)
  );
}

/**
 * Clear any lingering MSAL cookies from browsers that previously had
 * storeAuthStateInCookie enabled. Safe to call even when no cookies exist.
 */
export function clearMsalCookies(options: ClearMsalCookiesOptions = {}): void {
  document.cookie
    .split(';')
    .map(c => c.trim())
    .filter(c => c.startsWith('msal.'))
    .forEach(c => {
      const rawName = c.split('=')[0];
      try {
        const cookieName = decodeURIComponent(rawName);
        if (
          options.preserveEncryptionKey &&
          cookieName === MSAL_CACHE_ENCRYPTION_COOKIE
        ) {
          return;
        }
        document.cookie = `${encodeURIComponent(cookieName)}=;path=/;SameSite=Lax;expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      } catch {
        document.cookie = `${rawName}=;path=/;SameSite=Lax;expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      }
    });
}

/**
 * Clear all MSAL-related storage keys (localStorage + sessionStorage + cookies)
 */
export function clearMsalStorage(): void {
  getMsalStorageKeys().forEach(key => localStorage.removeItem(key));
  Object.keys(sessionStorage)
    .filter(key => key.startsWith('msal.'))
    .forEach(key => sessionStorage.removeItem(key));
  clearMsalCookies();
  sessionStorage.removeItem('auth-type');
  localStorage.removeItem('pending-auth-type');
  // Plaintext token copy written by previous app versions.
  localStorage.removeItem('id_token');
}
